import { z } from 'zod';
/**
 * Email templates (docs/06). The wording of each lives in the client profile
 * (`email.templates`) and, after the first deploy, in the append-only `email_templates` table
 * that Admin edits. The keys and the variables each template can use are the code's contract.
 */
export const INTERNAL_TEMPLATE_KEYS = [
    'job_assigned',
    'additional_issue',
    'void_request',
    'pin_lockout',
    'send_failed',
    'daily_cap',
    'ops_result',
    'daily_digest',
    'weekly_integrity',
    'test',
    'job_package',
];
/** Customer emails are manual only: preview, then Send with the preview token. */
export const CUSTOMER_TEMPLATE_KEYS = ['customer_received', 'customer_completed', 'customer_rental_out', 'customer_rental_in'];
export const EMAIL_TEMPLATE_KEYS = [...INTERNAL_TEMPLATE_KEYS, ...CUSTOMER_TEMPLATE_KEYS];
export const isCustomerTemplate = (k) => CUSTOMER_TEMPLATE_KEYS.includes(k);
const COMMON = ['appName', 'company.name', 'company.phone', 'company.addressLine', 'link'];
const JOB = ['number', 'customer', 'machine', 'serial', 'problem', 'location'];
const CUSTOMER = [...JOB, 'contact', 'customerPo'];
export const TEMPLATE_INFO = {
    job_assigned: { label: 'Job assigned', to: 'The mechanic (only if they have an email address)', vars: [...COMMON, ...JOB, 'mechanic'] },
    additional_issue: { label: 'Additional issue reported', to: 'Service manager', vars: [...COMMON, ...JOB, 'mechanic', 'note'] },
    void_request: { label: 'Void request', to: 'The approver (Manager or Admin who did not request it)', vars: [...COMMON, ...JOB, 'requestedBy', 'reason'] },
    pin_lockout: { label: 'Mechanic PIN locked', to: 'Service manager', vars: [...COMMON, 'mechanic', 'device', 'attempts', 'minutes'] },
    send_failed: { label: 'Email could not be sent', to: 'Service manager', vars: [...COMMON, 'number', 'to', 'subject', 'error', 'attempts'] },
    daily_cap: { label: 'Daily email limit reached', to: 'Owner', vars: [...COMMON, 'cap', 'queued', 'date'] },
    ops_result: { label: 'Backup / audit chain / restore test result', to: 'Owner', vars: [...COMMON, 'job', 'status', 'when', 'detail'] },
    daily_digest: { label: 'Daily open-jobs digest', to: 'Service manager', vars: [...COMMON, 'date', 'time', 'count', 'report'] },
    weekly_integrity: { label: 'Weekly integrity report', to: 'Service manager and owner', vars: [...COMMON, 'week', 'period', 'count', 'report'] },
    test: { label: 'Test email', to: 'Whoever the test is sent to', vars: [...COMMON, 'sentBy', 'when'] },
    job_package: {
        label: 'Closed job package (form PDFs, scans, office notes)',
        to: 'Billing (settings: job package recipients)',
        vars: [...COMMON, ...JOB, 'jobType', 'customerPo', 'closedAt', 'closedBy', 'attachments', 'internalNotes'],
    },
    customer_received: { label: 'Work order received', to: 'Customer contact', vars: [...COMMON, ...CUSTOMER] },
    customer_completed: { label: 'Job completed (the job report PDF attached)', to: 'Customer contact', vars: [...COMMON, ...CUSTOMER] },
    customer_rental_out: { label: 'Rental check-out (PDF attached)', to: 'Customer contact', vars: [...COMMON, ...CUSTOMER] },
    customer_rental_in: { label: 'Rental check-in (PDF attached)', to: 'Customer contact', vars: [...COMMON, ...CUSTOMER] },
};
const noNewline = (s) => !/[\r\n]/.test(s);
export const EmailTemplateText = z.object({
    subject: z.string().trim().min(1).max(300).refine(noNewline, 'Subject must be one line'),
    body: z.string().trim().min(1).max(20_000),
});
export const EmailAddress = z.string().trim().toLowerCase().email().max(254);
export const CustomerEmailPreviewInput = z.object({
    template: z.enum(CUSTOMER_TEMPLATE_KEYS),
    /** Defaults to the customer's contacts marked "receives email" (else the customer's email). */
    to: z.array(EmailAddress).min(1).max(10).optional(),
    subject: z.string().trim().min(1).max(300).refine(noNewline, 'Subject must be one line').optional(),
    body: z.string().trim().min(1).max(20_000).optional(),
});
export const CustomerEmailSendInput = z.object({ token: z.string().min(20).max(200) });
/** Admin: who gets which automatic email (settings.email). Empty list = the documented fallback. */
export const EmailRecipientsInput = z.object({
    serviceManagerEmails: z.array(EmailAddress).max(10),
    ownerEmails: z.array(EmailAddress).max(10),
    /** The closed-job package ("Skip"). Empty = no package email. */
    closeRecipients: z.array(EmailAddress).max(10),
});
export const EmailTestInput = z.object({ to: EmailAddress.optional() });
/** "Ryan" in the spec: the owner who gets infrastructure results. */
export const OPS_JOBS = ['backup', 'binlog', 'audit', 'restore', 'release'];
//# sourceMappingURL=email.js.map